import React, { useState, useEffect } from 'react';
import Icon from '../../shared/Icon';
import { apiService } from '../../../../services/api';
import './Step2CreateDataset.css';

/**
 * View Component for Step 2: Create Dataset
 * Shows uploaded files with editable dataset names and a "group as one dataset" toggle.
 * When grouped: all files go into one dataset (name from groupedDatasetName field).
 * When not grouped: each file becomes its own dataset using its editable name.
 *
 * Also exposes an "Add to" module dropdown — when selected, the wizard's
 * Next handler delegates to the per-file fan-out endpoint and ignores the
 * group/name fields (backend derives dataset names from file names).
 */
const Step2CreateDataset = ({ selectedFiles, formData, onSetFormData }) => {
  const files = selectedFiles || [];

  const [groupAsOne, setGroupAsOne] = useState(formData?.groupAsOneDataset ?? false);
  const [groupedDatasetName, setGroupedDatasetName] = useState(formData?.groupedDatasetName || '');
  const [fileDatasetNames, setFileDatasetNames] = useState(() => {
    if (formData?.fileDatasetNames?.length === files.length && files.length > 0) {
      return formData.fileDatasetNames;
    }
    return files.map((f) => f.name);
  });
  // Module dropdown state. null = working dataset (default).
  const [enabledModules, setEnabledModules] = useState([]);
  const [selectedModuleId, setSelectedModuleId] = useState(formData?.selectedModuleId || null);

  // Fetch enabled modules once on mount — silent on failure (the wizard works
  // fine as a plain dataset upload if /me/modules is unavailable).
  useEffect(() => {
    let cancelled = false;
    apiService
      .getMyModules()
      .then((resp) => { if (!cancelled) setEnabledModules(resp?.modules || []); })
      .catch(() => { if (!cancelled) setEnabledModules([]); });
    return () => { cancelled = true; };
  }, []);

  // Re-sync if parent pushes new state (e.g. navigating back)
  useEffect(() => {
    if (formData?.fileDatasetNames?.length === files.length && files.length > 0) {
      setFileDatasetNames(formData.fileDatasetNames);
    }
    if (formData?.groupAsOneDataset !== undefined) setGroupAsOne(formData.groupAsOneDataset);
    if (formData?.groupedDatasetName !== undefined) setGroupedDatasetName(formData.groupedDatasetName);
    if (formData?.selectedModuleId !== undefined) setSelectedModuleId(formData.selectedModuleId);
  }, [formData]);

  const propagate = (patch) => {
    if (onSetFormData) {
      onSetFormData({
        ...formData,
        fileDatasetNames,
        groupAsOneDataset: groupAsOne,
        groupedDatasetName,
        selectedModuleId,
        ...patch,
      });
    }
  };

  const handleModuleChange = (value) => {
    const next = value || null;
    setSelectedModuleId(next);
    propagate({ selectedModuleId: next });
  };

  const handleFileNameChange = (index, value) => {
    const updated = [...fileDatasetNames];
    updated[index] = value;
    setFileDatasetNames(updated);
    propagate({ fileDatasetNames: updated });
  };

  const handleGroupToggle = (checked) => {
    setGroupAsOne(checked);
    propagate({ groupAsOneDataset: checked });
  };

  const handleGroupedNameChange = (value) => {
    setGroupedDatasetName(value);
    propagate({ groupedDatasetName: value });
  };

  const moduleSelected = !!selectedModuleId;

  return (
    <div className="step-create-container">
      <h3 className="step-create-title">Edit names for your dataset</h3>

      {/* Radio choice between default dataset path and the module fan-out
          path. "Default dataset" → existing behavior. "Add to a module" →
          dropdown of enabled modules; selecting one routes the upload
          through the per-file fan-out endpoint. */}
      {enabledModules.length > 0 && (
        <div className="step-create-field">
          <label className="resui-radio">
            <input
              type="radio"
              name="step-create-target"
              className="resui-radio-input"
              checked={!moduleSelected}
              onChange={() => handleModuleChange(null)}
            />
            <span className="resui-radio-mark" />
            <span className="resui-radio-label">Default dataset</span>
          </label>
          <label className="resui-radio">
            <input
              type="radio"
              name="step-create-target"
              className="resui-radio-input"
              checked={moduleSelected}
              onChange={() => handleModuleChange(enabledModules[0]?.module_id || null)}
            />
            <span className="resui-radio-mark" />
            <span className="resui-radio-label">Add to a module</span>
          </label>
          {moduleSelected && (
            <>
              <label className="step-create-label">Module Name</label>
              <select
                className="resui-select"
                value={selectedModuleId || ''}
                onChange={(e) => handleModuleChange(e.target.value)}
              >
                {enabledModules.map((m) => (
                  <option key={m.module_id} value={m.module_id}>{m.name}</option>
                ))}
              </select>
              <p className="step-create-helper">
                Each file becomes its own dataset under this module. Names are
                derived from the file names.
              </p>
            </>
          )}
        </div>
      )}

      {!moduleSelected && (
        <label className="step-create-group-label resui-checkbox">
          <input
            type="checkbox"
            className="step-create-group-checkbox resui-checkbox-input"
            checked={groupAsOne}
            onChange={(e) => handleGroupToggle(e.target.checked)}
          />
          <span className="resui-checkbox-mark" />
          <span className="resui-checkbox-label">Group all files as one dataset</span>
        </label>
      )}

      {!moduleSelected && groupAsOne && (
        <div className="step-create-field">
          <label className="step-create-label">
            Dataset name<span className="step-create-required">*</span>
          </label>
          <input
            type="text"
            className="resui-input"
            placeholder="Enter a name for your combined dataset"
            value={groupedDatasetName}
            onChange={(e) => handleGroupedNameChange(e.target.value)}
          />
        </div>
      )}

      {!moduleSelected && (
        <div className="step-create-file-table">
          {files.map((file, index) => (
            <div key={index} className="step-create-file-row">
              <div className="step-create-file-original">
                <Icon name="description" className="step-create-file-icon" size={18} />
                <span className="step-create-file-original-name" title={file.name}>
                  {file.name}
                </span>
              </div>
              <input
                type="text"
                className={`resui-input ${groupAsOne ? 'step-create-input-disabled' : ''}`}
                value={fileDatasetNames[index] ?? file.name}
                onChange={(e) => handleFileNameChange(index, e.target.value)}
                disabled={groupAsOne}
                placeholder="Dataset name"
              />
            </div>
          ))}
        </div>
      )}

      {moduleSelected && (
        <div className="step-create-file-table">
          {files.map((file, index) => (
            <div key={index} className="step-create-file-row">
              <div className="step-create-file-original">
                <Icon name="description" className="step-create-file-icon" size={18} />
                <span className="step-create-file-original-name" title={file.name}>
                  {file.name}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Step2CreateDataset;
